import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import Big from 'big.js';

const conversionToPounds: Record<Unit, number> = {
  [enumUnit.Pounds]: 1,
  [enumUnit.Ounces]: 1 / 16,
  [enumUnit.NetTons]: 2000,
  [enumUnit.ShortTons]: 2000,
  [enumUnit.GrossTons]: 2240,
  [enumUnit.LongTons]: 2240,
  [enumUnit.Kilograms]: 2.2046226218,
  [enumUnit.Grams]: 0.00220462,
  [enumUnit.MetricTons]: 2204.6226218,
  [enumUnit.ShortHundredweight]: 100,
  [enumUnit.LongHundredweight]: 112,
  [enumUnit.Each]: 0,
  [enumUnit.Yard]: 0,
  [enumUnit.Meter]: 0,
};

export type Quantity = {
  value: number;
  unit: Unit;
};

export const convertQuantity = (
  value: number,
  fromUnit: Unit,
  toUnit: Unit,
): number => {
  if (fromUnit === toUnit) {
    return value;
  }

  if (fromUnit === enumUnit.Each || toUnit === enumUnit.Each) {
    return value;
  }

  const valueInPounds = new Big(value).times(conversionToPounds[fromUnit]);
  const targetFactor = conversionToPounds[toUnit];

  if (targetFactor === 0) {
    return value;
  }

  return valueInPounds.div(targetFactor).toNumber();
};

export const formatUnit = (unit: Unit): string => {
  switch (unit) {
    case enumUnit.Pounds:
      return 'lb';
    case enumUnit.Ounces:
      return 'oz';
    case enumUnit.Kilograms:
      return 'kg';
    case enumUnit.Grams:
      return 'g';
    case enumUnit.MetricTons:
      return 'MT';
    case enumUnit.ShortTons:
      return 'ST';
    case enumUnit.LongTons:
      return 'LT';
    case enumUnit.NetTons:
      return 'NT';
    case enumUnit.GrossTons:
      return 'GT';
    case enumUnit.ShortHundredweight:
      return 'cwt';
    case enumUnit.LongHundredweight:
      return 'lcwt';
    case enumUnit.Each:
      return 'ea';
    default:
      return unit;
  }
};

export const formatQuantity = (
  value: number,
  unit: Unit,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    showUnit?: boolean;
  },
): string => {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    showUnit = true,
  } = options ?? {};

  const formattedValue = value.toLocaleString(undefined, {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  if (!showUnit) {
    return formattedValue;
  }

  return `${formattedValue} ${formatUnit(unit)}`;
};

export const formatQuantityWithConversion = (
  value: number,
  fromUnit: Unit,
  toUnit: Unit | null,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    showUnit?: boolean;
  },
): string => {
  const targetUnit = toUnit ?? fromUnit;
  const convertedValue = toUnit
    ? convertQuantity(value, fromUnit, toUnit)
    : value;

  return formatQuantity(convertedValue, targetUnit, options);
};
